import {
  CurrencySettleOrderTokenType,
  CurrencyTokenType,
  useCurrencyContext
} from '@apeiron/currency'
import { NextImage } from '@apeiron/temp'
import { Box, ButtonBase, styled, Tooltip, TooltipProps } from '@mui/material'
import InfoSVG from '@public/icons/icon-info.svg'
import { EXCHANGE_FEE_MULTIPLIER } from '@src/constants/marketplaceOrder'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  margin-top: 0px;
  border-radius: 2px;
  background: #262829;
  width: 100%;
`

const Row = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
`

const LeftContainer = styled(Box)`
  font-size: 14px;
  color: #999;
  margin-right: 8px;
  display: flex;
`

const RightContainer = styled(Box)`
  font-size: 14px;
  color: #ffffff;
  text-align: right;
`

const TextTooltip = styled((props: TooltipProps) => {
  const { className, ...rest } = props

  return <Tooltip {...rest} componentsProps={{ tooltip: { className } }} />
})`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: '#FFFFFF';
  background-color: #696c6e;
`

const InfoButton = styled(ButtonBase)`
  margin-left: 9px;
  align-self: center;
`

const IconInfoImage = styled(NextImage)`
  width: 12px;
  height: 12px;
`

const LiquidtyProviderFeeDetail = () => {
  const { t } = useTranslation()

  return (
    <TextTooltip title={t('dialog.buy.liquidity_provider_fee_detail')}>
      <InfoButton disableRipple>
        <IconInfoImage src={InfoSVG} />
      </InfoButton>
    </TextTooltip>
  )
}

const routeMapping = {
  [CurrencyTokenType.Aprs]: [
    CurrencyTokenType.Aprs,
    CurrencyTokenType.Ron,
    CurrencyTokenType.Weth
  ],
  [CurrencyTokenType.Anima]: [
    CurrencyTokenType.Anima,
    CurrencyTokenType.Ron,
    CurrencyTokenType.Weth
  ],
  [CurrencyTokenType.Ron]: [CurrencyTokenType.Ron, CurrencyTokenType.Weth],
  [CurrencyTokenType.Usdc]: [CurrencyTokenType.Usdc, CurrencyTokenType.Weth],
  [CurrencyTokenType.Weth]: [CurrencyTokenType.Weth]
}

const ExchangeRateDetail = ({ price, settleOrderTokenType }: Props) => {
  const { t } = useTranslation()

  const { getApeironPrice } = useCurrencyContext()

  const liquidityProviderFee = getApeironPrice(null, price * 0.003, {
    allowedLargeNumberAbbr: false,
    maxNumberWithoutENotation: 5e9
  }) // 0.3% LIQUIDITY PROVIDER fee

  const maximumSent = getApeironPrice(null, price * EXCHANGE_FEE_MULTIPLIER, {
    allowedLargeNumberAbbr: false,
    maxNumberWithoutENotation: 5e9
  }) // 1% slippage + 0.3% LIQUIDITY PROVIDER fee

  return (
    <Container>
      <Row>
        <LeftContainer>
          {t('dialog.buy.liquidity_provider_fee')}
          <LiquidtyProviderFeeDetail />
        </LeftContainer>
        <RightContainer>
          ~{liquidityProviderFee} {settleOrderTokenType}
        </RightContainer>
      </Row>
      <Row>
        <LeftContainer>{t('dialog.buy.exchange_route')}</LeftContainer>
        <RightContainer>
          {routeMapping[settleOrderTokenType].join(' > ')}
        </RightContainer>
      </Row>
      <Row>
        <LeftContainer>{t('dialog.buy.maximum_sent')}</LeftContainer>
        <RightContainer>
          {maximumSent} {settleOrderTokenType}
        </RightContainer>
      </Row>
      <Row>
        <LeftContainer>{t('dialog.buy.slippage_tolerence')}</LeftContainer>
        <RightContainer>1%</RightContainer>
      </Row>
    </Container>
  )
}

type Props = {
  price: number
  settleOrderTokenType: CurrencySettleOrderTokenType
}

export default ExchangeRateDetail
